import { useEffect, useId, useState, type FormEvent } from 'react';
import { ApiError, apiGet, apiPost } from '../api/client';
import { apiBaseUrl } from '../api/runtime-config';
import { Alert } from '../ui/Alert/Alert';
import { Button } from '../ui/Button/Button';
import { Card } from '../ui/Card/Card';
import { Input } from '../ui/Input/Input';
import { AdminIcon, BoltIcon, BrandMark, CloudOutlineIcon, MicrosoftLogo, TeamsIcon } from '../app/icons';
import { AmbientFlow } from '../app/AmbientFlow';
import styles from './LoginScreen.module.css';
import { brandName } from '../api/runtime-config';

type LoginMode = 'loading' | 'sso' | 'dev';

/** Same wording as the API's single 401 (docs/SECURITY.md §6) — the screen never hints at which part was wrong. */
export const LOCAL_LOGIN_GENERIC_ERROR = 'Email, password or code is incorrect, or the account is locked.';
export const LOCAL_LOGIN_THROTTLED_ERROR = 'Too many attempts. Wait a few minutes and try again.';

const TAGLINE = 'Smarter Support. Faster Resolutions.';

const FEATURES = [
  { icon: BoltIcon, title: 'Fast resolution times', detail: 'Get back to work, sooner.' },
  { icon: AdminIcon, title: 'Secure & trusted', detail: 'Powered by Microsoft identity.' },
  { icon: TeamsIcon, title: 'Expert IT support team', detail: 'Real people. Real help.' },
  { icon: CloudOutlineIcon, title: 'Built for a better workplace', detail: 'Simple. Reliable. Always on.' },
] as const;

/**
 * Standalone (no sidebar/top bar) — this is the one screen an unauthenticated
 * visitor can reach.
 *
 * Two modes, chosen by the API (`GET /auth/mode`), never by the SPA:
 *  - `sso`: sign-in is a full page navigation to `GET /auth/login`
 *    (docs/API.md §9), which redirects to Entra ID and back through
 *    `/auth/callback`, where the API sets the httpOnly session cookie.
 *  - `dev`: the API runs the developer identity provider (a private
 *    single-server install, or local development). The user types the email
 *    of an existing account and the API sets the same httpOnly cookie. The
 *    SPA still never holds a token.
 *
 * If the mode call fails, the SSO button is shown — the safe default.
 *
 * Break-glass: when the API reports `localAdminLogin: true`, SSO mode also
 * offers a small secondary "IT administrator sign-in (local account)" link
 * that reveals an email + password + authenticator-code form posting to
 * `POST /auth/local-login`. It exists for when Entra ID is unavailable; the
 * API enforces TOTP, lockout and throttling, and answers every failure with
 * one generic message, which is all this screen ever shows.
 */
export default function LoginScreen() {
  const [mode, setMode] = useState<LoginMode>('loading');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [localAvailable, setLocalAvailable] = useState(false);
  const [localOpen, setLocalOpen] = useState(false);
  const [local, setLocal] = useState({ email: '', password: '', code: '' });
  const [localError, setLocalError] = useState<string | null>(null);
  const [localSubmitting, setLocalSubmitting] = useState(false);
  // Purely visual: set just before the full-page navigation to /auth/login.
  const [redirecting, setRedirecting] = useState(false);
  const localErrorId = useId();

  // Back/forward cache: if the visitor comes back from Entra with the browser
  // Back button, the page is restored as it was — spinner and all. Reset it.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setRedirecting(false);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  useEffect(() => {
    let cancelled = false;
    apiGet<{ mode: 'dev' | 'sso'; localAdminLogin?: boolean }>('/auth/mode')
      .then((r) => {
        if (cancelled) return;
        setMode(r.mode === 'dev' ? 'dev' : 'sso');
        setLocalAvailable(r.localAdminLogin === true);
      })
      .catch(() => {
        if (!cancelled) setMode('sso');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function returnToPath(): string {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get('returnTo');
    // Only same-origin relative paths; anything else goes to the dashboard.
    if (fromQuery && fromQuery.startsWith('/') && !fromQuery.startsWith('//')) return fromQuery;
    const here = `${window.location.pathname}${window.location.search}`;
    return here && !here.startsWith('/login') ? here : '/';
  }

  function signInSso(): void {
    // A full page navigation, not a fetch: the API answers with a 302 to
    // Entra, and only the browser can follow that. The API sanitises
    // `returnTo` against open redirects before using it.
    const returnTo = `${window.location.pathname}${window.location.search}`;
    const target = returnTo && returnTo !== '/login' ? `?returnTo=${encodeURIComponent(returnTo)}` : '';
    setRedirecting(true);
    window.location.assign(`${apiBaseUrl()}/auth/login${target}`);
  }

  async function signInDev(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Enter your email address.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await apiPost('/auth/dev-login', { email: trimmed });
      // Full reload so every cached query starts fresh under the new session.
      window.location.assign(returnToPath());
    } catch (err) {
      setSubmitting(false);
      if (err instanceof ApiError && err.status !== 0 && err.status < 500) {
        setError('That email does not match an active account.');
      } else {
        setError('Sign-in is unavailable right now. Try again in a moment.');
      }
    }
  }

  async function signInLocal(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const email = local.email.trim();
    const code = local.code.replace(/\s/g, '');
    if (!email || !local.password || !/^\d{6}$/.test(code)) {
      setLocalError('Enter your email, password and the 6-digit code from your authenticator app.');
      return;
    }
    setLocalSubmitting(true);
    setLocalError(null);
    try {
      await apiPost('/auth/local-login', { email, password: local.password, code });
      window.location.assign(returnToPath());
    } catch (err) {
      setLocalSubmitting(false);
      // A code is single-use and the password should not linger in the DOM.
      setLocal((prev) => ({ ...prev, password: '', code: '' }));
      if (err instanceof ApiError && err.status === 429) setLocalError(LOCAL_LOGIN_THROTTLED_ERROR);
      else if (err instanceof ApiError && err.status !== 0 && err.status < 500) setLocalError(LOCAL_LOGIN_GENERIC_ERROR);
      else setLocalError('Sign-in is unavailable right now. Try again in a moment.');
    }
  }


  const brand = brandName();
  const sso = mode !== 'dev';

  return (
    <div className={styles.page}>
      {/* Decorative brand waves (the same system as the app shell, at full strength). Hidden from assistive tech. */}
      <AmbientFlow variant="hero" />

      <header className={styles.topbar}>
        <div className={styles.brand}>
          <BrandMark className={styles.brandMark} />
          <span className={styles.brandName}>{brand}</span>
          <span className={styles.brandTagline}>{TAGLINE}</span>
        </div>
        <p className={styles.status}>
          <AdminIcon className={styles.statusIcon} />
          <span>Secure • Enterprise Ready</span>
        </p>
      </header>

      <main className={styles.main}>
        <Card className={styles.card}>
          <div className={styles.cardHead}>
            <BrandMark className={styles.cardMark} />
            <h1 className={styles.title}>{brand}</h1>
            <p className={styles.tagline}>{TAGLINE}</p>
          </div>

          {mode === 'dev' ? (
            <form onSubmit={(e) => void signInDev(e)} noValidate>
              <p className={styles.description}>Enter the email address of your helpdesk account.</p>
              <div className={styles.field}>
                <Input
                  label="Email"
                  type="email"
                  name="email"
                  autoComplete="username"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  {...(error ? { error } : {})}
                />
              </div>
              <Button type="submit" variant="primary" size="lg" fullWidth disabled={submitting} className={styles.primaryAction}>
                {submitting ? 'Signing in…' : 'Sign in'}
              </Button>
              <div className={styles.notice}>
                <Alert tone="warning">Private server sign-in: no password is checked. Only people on the allowed network can reach this page.</Alert>
              </div>
            </form>
          ) : (
            <>
              <p className={styles.description}>
                Sign in with your Microsoft account to access your helpdesk portal, view tickets, and get help from your IT team.
              </p>
              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={signInSso}
                disabled={mode === 'loading'}
                loading={redirecting}
                leftIcon={<MicrosoftLogo />}
                className={styles.primaryAction}
              >
                Sign in with Microsoft
              </Button>
              <p className={styles.divider}>
                <span>Secure • Microsoft Identity</span>
              </p>
              {localAvailable && (
                <div className={styles.local}>
                  <button
                    type="button"
                    className={styles.localToggle}
                    aria-expanded={localOpen}
                    aria-controls="local-admin-signin"
                    onClick={() => setLocalOpen((open) => !open)}
                  >
                    <AdminIcon className={styles.localIcon} />
                    <span>IT administrator sign-in (local account)</span>
                  </button>
                  {localOpen && (
                    <form
                      id="local-admin-signin"
                      className={styles.localForm}
                      onSubmit={(e) => void signInLocal(e)}
                      noValidate
                      aria-label="IT administrator local sign-in"
                      {...(localError ? { 'aria-describedby': localErrorId } : {})}
                    >
                      <p className={styles.localHint}>For emergency access when Microsoft sign-in is unavailable. Requires the authenticator app enrolled for this account.</p>
                      <div className={styles.field}>
                        <Input
                          label="Email"
                          type="email"
                          name="local-email"
                          autoComplete="username"
                          autoFocus
                          value={local.email}
                          onChange={(e) => setLocal((prev) => ({ ...prev, email: e.target.value }))}
                        />
                      </div>
                      <div className={styles.field}>
                        <Input
                          label="Password"
                          type="password"
                          name="local-password"
                          autoComplete="current-password"
                          value={local.password}
                          onChange={(e) => setLocal((prev) => ({ ...prev, password: e.target.value }))}
                        />
                      </div>
                      <div className={styles.field}>
                        <Input
                          label="Authenticator code"
                          name="local-code"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          pattern="[0-9]{6}"
                          maxLength={6}
                          hint="The 6-digit code from your authenticator app."
                          value={local.code}
                          onChange={(e) => setLocal((prev) => ({ ...prev, code: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                        />
                      </div>
                      {localError && (
                        <div className={styles.field}>
                          <Alert tone="danger" id={localErrorId}>
                            {localError}
                          </Alert>
                        </div>
                      )}
                      <Button type="submit" variant="secondary" size="lg" fullWidth disabled={localSubmitting} className={styles.secondaryAction}>
                        {localSubmitting ? 'Signing in…' : 'Sign in with local account'}
                      </Button>
                    </form>
                  )}
                </div>
              )}
            </>
          )}
        </Card>

        <section className={styles.hero} aria-labelledby="login-hero-wordmark">
          <p id="login-hero-wordmark" className={styles.wordmark}>
            {brand}
          </p>
          <p className={styles.heroLead}>
            Get help, report issues, and stay productive.{' '}
            <br />
            Our IT team is here to support you.
          </p>
          <ul className={styles.features}>
            {FEATURES.map(({ icon: Icon, title, detail }) => (
              <li key={title} className={styles.feature}>
                <span className={styles.featureIcon}>
                  <Icon />
                </span>
                <span>
                  <span className={styles.featureTitle}>{title}</span>
                  <span className={styles.featureDetail}>{detail}</span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      </main>

      <footer className={styles.footer}>
        <span>{brand}</span>
        <span className={styles.footerSep} aria-hidden="true">
          |
        </span>
        <span>Powered by Girish</span>
      </footer>
    </div>
  );
}
